import React, {useEffect, useState} from "react";
import { useDispatch, useSelector } from 'react-redux';
import Page from '../../../page';
import { setScreen } from '../../../../redux/connectedComponent/uiFactory/uiFactory.slice';
import { screens } from '../../../config/uiFactory.config';
import SolutionHeader from '../../SolutionHeader/SolutionHeader';
import LearnMore from '../../../LearnMore/LearnMore';
import {getDepartments, getConnectionsAndAccessGrants} from '../../../../redux/connectedComponent/apiIntegrations/apiIntegration.slice';
import { Button, Loader } from 'cdk-radial';
import DMSDropdown from '../../../stepper/stepperBody/stepperBodyComponents/DMSSelectionComponents/DMSDropdown/DMSDropdown';
import {
    updateDMSAccountsDetailsForApp,
} from '../../../../redux/connectedComponent/updateDMSAccounts/updateDMSAccounts.slice';
import Notification from '../../../notification/Notification';
import { sendAmplitudeData } from "../../../../utils/amplitude";
import { AMPLITUDE_EVENTS, AMPLITUDE_PROPERTIES } from "../../../../constants/amplitudeConstants";
import { trackPageView } from '../../../../utils/gtm';
const { DMS_ACCOUNT_NAME_TYPE_MAPPING } = require('../../../stepper/stepperBody/stepperBodyComponents/DMSSelectionComponents/utils/constant');
const DMS_ACCOUNTS = 'Manage DMS Accounts';
const DMS_ACCOUNTS_DESCRIPTION = 'Select the DMS accounts to use with this app.';
const POSITIVE = 'positive';
const NEGATIVE = 'negative';

export const EditDMSAccountsV2 = ({
    solutionData,
    additionalData: { subscriptionData }
}) => {
    const { id: solutionId, registration_details: { api_details } } = solutionData;

    const dispatch = useDispatch();
    const {
        authenticationToken,
        entityIdNameMapping,
        entityIdAddressMapping
    } = useSelector(state => state.loginDetailsSlice);
    const {
        isDepartmentsPending,
        isConnectionsAGPending,
        departments,
        connectionsAGs,
        errorInFetchingDepartments,
        errorInFetchingConnectionsAGs
    } = useSelector(state => state.apiIntegrationSlice);
    const {
        isDMSAccountsUpdateSuccess,
        isDMSAccountsUpdateLoading,
        errorInDMSAccountsUpdate
    } = useSelector(state => state.updateDMSAccountsSlice);

    const onBackButtonClick = () => dispatch(setScreen({ screen: screens.SUBSCRIPTION_MANAGEMENT }));

    const [userSelectedDMSAccounts, setUserSelectedDMSAccounts] = useState({});
    const [connectionIds, setConnectionIds] = useState([]);

    useEffect(() => {
        trackPageView('Manage DMS Accounts');
    }, []);

    const [showNotification, setShowNotification] = useState(false);
    const [notificationContent, setNotificationContent] = useState(false);
    const [notificationType, setNotificationType] = useState(false);
    const [dmsAccountTypesAndSupportedAPIs, setDmsAccountTypesAndSupportedAPIs] = useState({});
    const [apiIdNameMapping, setApiIdNameMapping] = useState({});
    const [error, setError] = useState('');

    useEffect(() => {
        const appName = solutionData?.overview?.name;
        if(isDMSAccountsUpdateSuccess) {
            setNotificationContent(
                `You updated DMS Accounts for app ${appName} successfully`
            );
            setNotificationType(POSITIVE);
            setShowNotification(true);
        } else if(errorInDMSAccountsUpdate) {
            setNotificationContent(`Failed to update DMS Accounts for app ${appName}, please try again`);
            setNotificationType(NEGATIVE);
            setShowNotification(true);
        } else {
            setShowNotification(false);
        }
    }, [isDMSAccountsUpdateSuccess, errorInDMSAccountsUpdate]);

    useEffect(() => {
        if(isDepartmentsPending || isConnectionsAGPending) {
            return;
        }

        let dmsAccountsSetByTheUser = {},
        dmsAccountsAndSupportingAPIs = {},
        connections = {}, apiIdAndNames = {};

        connectionsAGs.forEach(connection => {
            const {api, entityId, connectionId, accessGrantId} = connection;
            const apiDetails = api_details.find(a => a.id === api);
            apiIdAndNames[api] = apiDetails?.displayName;
            connections[connectionId || accessGrantId] = {
              apiId: api,
              entityId,
              isAsyncAPI: apiDetails?.apiType === 'async-api',
              apiName: apiDetails?.displayName,
              asyncIntegrationId: apiDetails?.asyncIntegrationId || '',
            };
            setConnectionIds(connections);
            connection?.departments?.forEach(dep => {
                const {id, name, dmsType} = dep;
                if(!dmsAccountsSetByTheUser.hasOwnProperty(dmsType)) {
                    dmsAccountsSetByTheUser[dmsType] = [];
                }
                if(!dmsAccountsAndSupportingAPIs.hasOwnProperty(dmsType)) {
                    dmsAccountsAndSupportingAPIs[dmsType] = [];
                }
                dmsAccountsSetByTheUser?.[dmsType]?.push({
                    apiId: api,
                    id,
                    name,
                    entityId,
                    apiName: apiDetails?.displayName,
                    location: entityIdNameMapping[entityId],
                    dmsType
                });

                !dmsAccountsAndSupportingAPIs[dmsType].includes(api) && dmsAccountsAndSupportingAPIs?.[dmsType]?.push(api);
            })
        });
        setApiIdNameMapping(apiIdAndNames);
        setDmsAccountTypesAndSupportedAPIs(dmsAccountsAndSupportingAPIs);
        setUserSelectedDMSAccounts(dmsAccountsSetByTheUser);
    }, [departments, connectionsAGs]);

    useEffect(() => {
        dispatch(getDepartments({
            token: authenticationToken,
            entityIds: subscriptionData?.map(subscription => subscription?.entityId)?.toString()
        }));

        dispatch(getConnectionsAndAccessGrants({
            payload: {
                appId: solutionId,
                entityIds: subscriptionData?.map(subscription => subscription?.entityId)
            },
            token: authenticationToken,
        }));
    }, [solutionId]);

    const handleSelectDMS = (location, dmsAccount, newDepartments, cmfNumber, dmsAccountType, entityId, type, id, api) => {
        let newDMSAccounts = userSelectedDMSAccounts[dmsAccountType].filter(dmsAccount => dmsAccount.entityId !== entityId);
        newDepartments.forEach(department => {
            dmsAccountTypesAndSupportedAPIs?.[dmsAccountType]?.forEach(apiId => {
                newDMSAccounts.push({
                    apiId,
                    apiName: api_details.find(a => a.id === apiId)?.displayName,
                    dmsType: dmsAccountType,
                    entityId,
                    id: department.id,
                    location,
                    name: department.name
                });
            })
        });

        const newDeps = {
            ...userSelectedDMSAccounts,
            [dmsAccountType]: newDMSAccounts
        }

        const newDepsKeys = Object.keys(newDeps);
        let emptyDMSAccounts = [];
        newDepsKeys.forEach(dmsAccountType => {
            if(!newDeps[dmsAccountType].length) {
                emptyDMSAccounts.push(dmsAccountType);
            };
        })

        emptyDMSAccounts.length ?
            setError(`DMS Accounts cannot be empty: ${emptyDMSAccounts.toString()}`) :
            setError('');

        setUserSelectedDMSAccounts(newDeps);
      };

    const renderDealerships = ({dmsAccountType, selectedDMS}) => {
        return departments.map(deps => {
            let entityId = Object.keys(deps)[0];
            let selectedDMSAccounts = selectedDMS.filter(
                dmsAccount =>
                    dmsAccount.entityId === entityId &&
                    dmsAccount.dmsType === dmsAccountType
            );
            return (
                <DMSDropdown
                    key={entityId}
                    location={entityIdNameMapping[entityId]}
                    address={entityIdAddressMapping[entityId]}
                    dmsAccount={{}}
                    dmsAccountType={dmsAccountType}
                    departments={deps[entityId]}
                    entityId={entityId}
                    handleSelectDMS={handleSelectDMS}
                    id={''}
                    selectedDMS={{}}
                    selectedDepartments={selectedDMSAccounts}
                    type={DMS_ACCOUNT_NAME_TYPE_MAPPING[dmsAccountType]}
                    disableSave={()=>{}}
                />
            );
        });
    };

    const onSave = () => {
        let editPayload = {
            dmsConfig: {},
            dmsConfigAsyncApi: {}
        };

        const keys = Object.keys(connectionIds);

        keys.forEach(id => {
            if(connectionIds[id].isAsyncAPI) {
                editPayload.dmsConfigAsyncApi[id] = {
                  integrationId: connectionIds[id]?.asyncIntegrationId,
                  departments: [],
                };
            }
            else {
                editPayload.dmsConfig[id] = []
            }
        });

        Object.keys(userSelectedDMSAccounts).forEach(dmsAccountType => {
            const dmsAccount = userSelectedDMSAccounts[dmsAccountType];
            if(dmsAccount.length) {
                dmsAccount.forEach(dmsAccount => {
                    const {name, id, dmsType} = dmsAccount;
                    const connectionId = keys.find(
                                            connectionId =>
                                                connectionIds[connectionId].entityId === dmsAccount.entityId &&
                                                connectionIds[connectionId].apiId === dmsAccount.apiId
                                            );
                    const payload = {name, id, dmsType};
                    connectionIds[connectionId].isAsyncAPI
                      ? editPayload.dmsConfigAsyncApi[
                          connectionId
                        ].departments.push(payload)
                      : editPayload.dmsConfig[connectionId].push(payload);
                });
            };
        });

        dispatch(updateDMSAccountsDetailsForApp({
            token: authenticationToken,
            solutionId,
            updatedDMSAcounts: editPayload,
            isNewAPI: false,
            bulkSubscribed: true
        }));

        sendAmplitudeData(AMPLITUDE_EVENTS.MANAGE_DMS_ACCOUNTS,{
            [AMPLITUDE_PROPERTIES.APP_NAME] : solutionData?.overview?.name || solutionData?.displayName,
            [AMPLITUDE_PROPERTIES.APP_ID]: solutionData?.id
        })
    }

    useEffect(() => {
        if(errorInFetchingDepartments || errorInFetchingConnectionsAGs) {
            setError('Error in loading data');
        }
    }, [errorInFetchingDepartments, errorInFetchingConnectionsAGs]);

    return (
        <Page>
            <SolutionHeader
                solutionData={solutionData}
                onBackButtonClickHandler={onBackButtonClick}
                appName={solutionData?.overview?.name}
                publisherName={solutionData?.orgName}
            />
            <p className="dms-header-title-display">{DMS_ACCOUNTS}</p>
            <p className="dms-header-subtitle-display">
                {DMS_ACCOUNTS_DESCRIPTION}{' '}
                <LearnMore
                  docPath="/docs/marketplace/marketplace-for-apps/managing-app-configurations/#managing-dms-accounts"
                  screen="DMS Configuration"
                />
            </p>
            {
                error && <div className="dms-accounts-error">{error}</div>
            }
            {
                (isDepartmentsPending || isConnectionsAGPending) ?
                    (
                        <div className="dms-loader">
                            <Loader label="Fetching DMS Accounts Details..." />
                        </div>
                    ) :
                    !isDMSAccountsUpdateLoading &&
                        Object.keys(userSelectedDMSAccounts)?.map(dmsAccountType => {
                            const dmsAccount = userSelectedDMSAccounts[dmsAccountType];
                            const apis = dmsAccountTypesAndSupportedAPIs[dmsAccountType]?.map(apiId => apiIdNameMapping[apiId]).join(', ');
                            return <div className="select-dms-account-container" key={dmsAccountType}>
                                <div className="dms-header">
                                    <div className="dms-header-title">
                                        {`${dmsAccountType} DMS Account`}
                                    </div>
                                    <div className="dms-card-header-subtitle">
                                        {apis}
                                    </div>
                                </div>
                                {renderDealerships({
                                    dmsAccountType,
                                    selectedDMS: dmsAccount
                                })}
                            </div>;
                        })
            }
            {(!isDepartmentsPending && !isConnectionsAGPending && !isDMSAccountsUpdateLoading) && (
                <div className="dms-next-button">
                    <Button
                        backgroundAppearance="light"
                        iconColor="primary"
                        iconSize={24}
                        isDisabled={error}
                        onClick={onSave}
                        size="standard"
                        text="Save"
                    />
                </div>
            )}
            {isDMSAccountsUpdateLoading && (
                <div className="dms-loader">
                    <Loader label="Updating DMS Accounts Details..." />
                </div>
            )}
            {
                showNotification &&
                <Notification
                    content={notificationContent}
                    variant={notificationType}
                    actions={[{"text":"Dismiss"}]}
                    onDismiss={() => setShowNotification(false)}
                    customStyle={{ marginTop: '70px' }}
                />
            }
        </Page>
    )
}
